/**
 * OrgChart — TekStak wrapper around the shared @techstack/ui OrgChart.
 *
 * The chart itself (drag-to-reparent, bench tray, merge-on-drop, two-click
 * delete, SVG connectors, optimistic moves, the ghosted suggestion tree with
 * confirm / merge / bench / reject and drag-a-ghost-onto-a-manager) lives in
 * packages/ui. This wrapper owns the app couplings and is the TekStak twin
 * of the GAS `OrgPanel` (apps/gas/frontend/src/App.jsx) — same capabilities,
 * same order of operations, JWT instead of the GAS bridge:
 *
 *   - maps the workspace API's snake_case contact rows onto the
 *     @techstack/core contract fields the shared component speaks;
 *   - persists lifecycle actions via /api/workspace (optimistic, revert on
 *     reject — UI-responsiveness review 2026-08-08);
 *   - people discovery (Layer 1, human-confirmed): ghost suggestions from
 *     /api/workspace/people-discovery, the manual "Discover people" pass,
 *     paste-a-directory and paste-a-roster seeds, the remembered discovered
 *     structure with "Apply reporting lines", bulk add-all with a dry-run
 *     preview + confidence threshold + undo;
 *   - "pull an existing CRM contact" (search + re-home), trash + restore,
 *     fullscreen overlay, and the empty-state canvas.
 *
 * Discovery reads the PUBLIC web server-side — no tenant data is involved,
 * which is why this surface is not GAS-only (DATA-BOUNDARY-POLICY).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OrgChart as SharedOrgChart } from '@techstack/ui';
import '@techstack/ui/dist/styles/org-chart.css';
import {
  ClipboardPaste, GitBranch, Maximize2, Minimize2, Network, Search, Sparkles,
  Table2, Trash2, Undo2, UserPlus, Users, X,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { toast } from '../Toast';

const humanErr = (e) => String(e?.message || e || 'Something went wrong');

/** Parse a pasted public directory ("Name, Title" per line) into seed rows. */
export function parseSeed(text) {
  return String(text || '').split('\n').map((l) => l.trim()).filter(Boolean).map((line) => {
    const idx = line.search(/[\t|,]/);
    if (idx < 0) return null;
    const name = line.slice(0, idx).trim();
    const title = line.slice(idx + 1).trim();
    return name && title ? { name, title } : null;
  }).filter(Boolean);
}

const STRUCT_STATUS = {
  confirmed: { label: 'on chart', tone: 'ok' },
  merged: { label: 'linked', tone: 'ok' },
  suggested: { label: 'pending', tone: 'warn' },
  rejected: { label: 'dismissed', tone: 'muted' },
};

function OrgChart({
  contacts,
  accountId,
  departmentId,
  departmentLabel,
  onChanged,
  onAddContact,
  onContactClick,
  fullscreen = false,
  // Pass-through to the shared canvas: the People split view's directory asks
  // it to isolate one person's branch. See SharedOrgChart's focusRequest.
  focusRequest = null,
  /*
   * WHICH people the roster under the canvas lists.
   *
   * 'all' is the standing behaviour and is right wherever the chart is the
   * only view of these people — /workspace, and the board widget.
   *
   * 'off-account' is for the People split view, where a contacts DIRECTORY
   * sits in the next pane. Measured there on Secretary of State 2026-09-18:
   * all five roster rows were the same five people the directory was already
   * showing, 240px of duplicate list whose top edge sat 27px BELOW the fold
   * on a 1440x900 screen. Reaching it meant scrolling past the canvas, which
   * is the gesture the split view exists to remove.
   *
   * What the directory CANNOT show is a contact homed to another account, so
   * that is what is left here. Each pane shows what the other does not.
   */
  rosterScope = 'all',
}) {
  const byId = useMemo(() => new Map(contacts.map((c) => [c.id, c])), [contacts]);
  const [trash, setTrash] = useState(null); // null = section closed; array = open
  const [busy, setBusy] = useState(false);
  const [fs, setFs] = useState(false); // fullscreen overlay (Esc leaves)

  // People discovery (Layer 1): ghosted suggestions + the manual pass. No
  // cron — a human triggers it and confirms every result.
  const [sugg, setSugg] = useState([]);
  const [discovering, setDiscovering] = useState(false);
  const [discoverResult, setDiscoverResult] = useState(null); // { found, matched, new, providers, stats }
  const [seedOpen, setSeedOpen] = useState(false);
  const [seedText, setSeedText] = useState('');
  const [compOpen, setCompOpen] = useState(false);
  const [compText, setCompText] = useState('');
  const [rosterBackbone, setRosterBackbone] = useState(null);
  const [structure, setStructure] = useState(null); // { people, stats } | null = closed
  const [structLoading, setStructLoading] = useState(false);
  const [applyingStruct, setApplyingStruct] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null); // { loading, threshold, data } | null
  const [bulkBusy, setBulkBusy] = useState(false);
  const [lastBulk, setLastBulk] = useState(null); // { kind, created, resolved, wiredChildIds, label } | null
  const [undoBusy, setUndoBusy] = useState(false);
  // The company-contacts bucket: everyone the CRM holds for this company, on
  // the canvas or not, so there is always a roster to grab from. Read from
  // /contacts/company by account/department id — a path that does NOT go
  // through account-name resolution, so it answers even when the canvas
  // can't (see the resolver note in services/chat/tools.js).
  const [roster, setRoster] = useState(null); // CompanyContact[] | null = not loaded
  const [rosterErr, setRosterErr] = useState(false);
  // CRM-wide search, subordinate to the bucket it lives in.
  const [pullOpen, setPullOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);

  const canDiscover = !!departmentId;
  const deptQs = `department_id=${encodeURIComponent(departmentId || '')}`;

  useEffect(() => {
    if (!fs) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setFs(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fs]);

  // Ghost suggestions are uncached server-side; reload after every discovery
  // or confirm/merge/reject so the canvas reflects the queue immediately.
  const refreshSugg = useCallback(() => {
    if (!departmentId) { setSugg([]); return Promise.resolve(); }
    return apiClient.get(`/api/workspace/people-discovery/suggestions?${deptQs}`)
      .then((r) => setSugg(r?.suggestions || []))
      .catch(() => setSugg([]));
  }, [departmentId, deptQs]);

  // Everyone the CRM ties to this company. Reloaded on every account switch
  // and after a pull; placement chips for people already on this account come
  // from the live `contacts` prop below, so an optimistic bench/delete shows
  // instantly without a second round trip.
  const loadRoster = useCallback(() => {
    if (!accountId && !departmentId) { setRoster(null); return Promise.resolve(); }
    const qs = new URLSearchParams();
    if (accountId) qs.set('account_id', accountId);
    if (departmentId) qs.set('department_id', String(departmentId));
    return apiClient.get(`/api/workspace/contacts/company?${qs.toString()}`)
      .then((r) => { setRoster(r?.contacts || []); setRosterErr(false); })
      .catch(() => { setRoster([]); setRosterErr(true); });
  }, [accountId, departmentId]);

  // Switching accounts resets every panel — a stale roster or preview from
  // the last agency must never sit over this one's chart.
  useEffect(() => {
    setTrash(null); setSugg([]); setDiscoverResult(null); setSeedOpen(false); setSeedText('');
    setCompOpen(false); setCompText(''); setRosterBackbone(null); setStructure(null);
    setBulkPreview(null); setLastBulk(null); setPullOpen(false); setQ(''); setResults(null);
    setRoster(null); setRosterErr(false);
    refreshSugg();
  }, [departmentId, refreshSugg]);

  useEffect(() => { loadRoster(); }, [loadRoster, contacts.length]);

  // Optimistic overlay (UI-responsiveness review 2026-08-08): delete/merge
  // drop the node and stash benches it the instant the user acts — the
  // parent's workspace refetch is 2 round-trips away. Entries self-heal:
  // once the contacts prop reflects the change (row gone / stashed_at set),
  // the overlay entry is redundant and gets dropped.
  const [gone, setGone] = useState({});     // id → true (deleted/merged away)
  const [benched, setBenched] = useState({}); // id → true (stashed)
  useEffect(() => {
    setGone((g) => {
      const stale = Object.keys(g).filter((id) => !contacts.some((c) => c.id === id));
      if (!stale.length) return g;
      const next = { ...g };
      stale.forEach((id) => delete next[id]);
      return next;
    });
    setBenched((b) => {
      const stale = Object.keys(b).filter((id) => {
        const c = contacts.find((x) => x.id === id);
        return !c || c.stashed_at;
      });
      if (!stale.length) return b;
      const next = { ...b };
      stale.forEach((id) => delete next[id]);
      return next;
    });
  }, [contacts]);

  const mapped = useMemo(
    () =>
      contacts
        .filter((c) => !gone[c.id])
        .map((c) => ({
          id: c.id,
          firstName: c.first_name,
          lastName: c.last_name,
          title: c.title,
          email: c.email,
          phone: c.phone,
          linkedinUrl: c.linkedin_url,
          relationship: c.relationship_status,
          influence: c.influence_level,
          reportsToId: benched[c.id] ? null : c.reports_to_id,
          stashedAt: benched[c.id] ? new Date().toISOString() : c.stashed_at,
          // Decision #22: unit groups the bench; provenance drives the pips.
          orgUnit: c.org_unit,
          source: c.source,
          provenance: c.field_provenance || {},
          flags: c.flags || [],
        })),
    [contacts, gone, benched]
  );

  /* ── confirmed-contact lifecycle ─────────────────────────────────────── */

  const handleReparent = async (contactId, newManagerId) => {
    await apiClient.put(`/api/workspace/contacts/${contactId}`, { reports_to_id: newManagerId });
    onChanged?.();
  };

  const loadTrash = useCallback(async () => {
    if (!accountId) { setTrash([]); return; }
    try {
      const r = await apiClient.get(`/api/workspace/contacts/trash?account_id=${accountId}`);
      setTrash(r?.contacts || []);
    } catch {
      setTrash([]);
    }
  }, [accountId]);

  const handleRestore = async (contactId) => {
    // The row leaves the tray NOW; a failed restore reloads the tray.
    setTrash((t) => (t ? t.filter((c) => c.id !== contactId) : t));
    try {
      await apiClient.post(`/api/workspace/contacts/${contactId}/restore`, {});
    } catch (e) {
      loadTrash();
      toast(humanErr(e), 'error');
      throw e;
    }
    toast('Restored — the contact is on the bench, drag them into place.');
    onChanged?.();
    if (trash) loadTrash();
  };

  const handleDelete = async (contactId) => {
    setGone((g) => ({ ...g, [contactId]: true }));
    try {
      await apiClient.del(`/api/workspace/contacts/${contactId}`);
    } catch (e) {
      setGone((g) => { const n = { ...g }; delete n[contactId]; return n; });
      throw e;
    }
    toast({ message: 'Moved to trash.', action: { label: 'Undo', run: () => handleRestore(contactId).catch(() => {}) } });
    onChanged?.();
    if (trash) loadTrash();
  };

  const handleStash = async (contactId) => {
    setBenched((b) => ({ ...b, [contactId]: true }));
    try {
      await apiClient.post(`/api/workspace/contacts/${contactId}/stash`, {});
    } catch (e) {
      setBenched((b) => { const n = { ...b }; delete n[contactId]; return n; });
      throw e;
    }
    onChanged?.();
  };

  const handleMerge = async (targetId, sourceId) => {
    setGone((g) => ({ ...g, [sourceId]: true }));
    try {
      await apiClient.post(`/api/workspace/contacts/${targetId}/merge`, { source_id: sourceId });
    } catch (e) {
      setGone((g) => { const n = { ...g }; delete n[sourceId]; return n; });
      throw e;
    }
    toast('Merged — duplicate absorbed (recoverable from trash).');
    onChanged?.();
    if (trash) loadTrash();
  };

  // Verify / Field: the chart flips the pips optimistically and reverts on
  // throw; the workspace refetch behind carries the server's stamp (actor,
  // time) into place.
  const handleVerify = async (contactId, status, fields) => {
    await apiClient.post(`/api/workspace/contacts/${contactId}/verify`, { status, fields });
    onChanged?.();
  };

  /* ── people discovery ───────────────────────────────────────────────── */

  const discover = () => {
    if (!canDiscover) return;
    setDiscovering(true); setDiscoverResult(null); setRosterBackbone(null);
    const seed = parseSeed(seedText);
    const comp = compText.trim();
    const body = { department_id: departmentId };
    if (seed.length) body.seed = seed;
    if (comp) body.compensation_csv = comp;
    apiClient.post('/api/workspace/people-discovery/run', body)
      .then((data) => {
        setDiscoverResult(data);
        setRosterBackbone(data.rosterBackbone || null);
        const dm = data.providers && data.providers.discovery;
        const noProvider = dm && !dm.search_api && !dm.llm_public_knowledge && !seed.length;
        if (data.found === 0 && noProvider && !comp) {
          toast('No discovery provider configured and no directory pasted — paste a leadership directory, or set a search provider key.', 'error');
        } else if (data.rosterBackbone) {
          const t = data.rosterBackbone.totals || {};
          toast(`Discovered ${data.found}; roster backbone: ${t.itPositions || 0} IT positions, ${t.techLeaders || 0} leaders.`);
        } else {
          toast(`Discovered ${data.found} — matched ${data.matched} existing, ${data.new} new.`);
        }
        setSeedOpen(false); setSeedText('');
        setCompOpen(false); setCompText('');
        refreshSugg();
      })
      .catch((e) => toast(humanErr(e), 'error'))
      .finally(() => setDiscovering(false));
  };

  // Confirm/merge/reject return promises so the ghost card shows its own busy
  // state and inline error. Optimistic: drop the suggestion from the rail
  // instantly, reconcile behind; restore the row on failure. Confirm/merge
  // refetch contacts once to bring the new/linked node onto the canvas.
  const restoreSugg = (removed) => {
    if (removed) setSugg((s) => (s.some((x) => x.id === removed.id) ? s : [...s, removed]));
  };
  const dropSugg = (id) => {
    const removed = sugg.find((s) => s.id === id);
    setSugg((s) => s.filter((x) => x.id !== id));
    return removed;
  };
  const confirmSugg = (id) => {
    const removed = dropSugg(id);
    return apiClient.post(`/api/workspace/people-discovery/${id}/confirm`, {})
      .then(() => { toast('Added to the org chart.'); onChanged?.(); })
      .catch((e) => { restoreSugg(removed); throw e; });
  };
  const mergeSugg = (id) => {
    const removed = dropSugg(id);
    return apiClient.post(`/api/workspace/people-discovery/${id}/merge`, {})
      .then(() => { toast('Linked to the existing contact.'); onChanged?.(); })
      .catch((e) => { restoreSugg(removed); throw e; });
  };
  const rejectSugg = (id) => {
    const removed = dropSugg(id);
    return apiClient.post(`/api/workspace/people-discovery/${id}/reject`, {})
      .catch((e) => { restoreSugg(removed); throw e; });
  };
  // Drag a ghost onto a real manager → create the contact wired under them
  // (managerId null = top level).
  const placeSugg = (id, managerId) => {
    const removed = dropSugg(id);
    return apiClient.post(`/api/workspace/people-discovery/${id}/confirm`, managerId ? { manager_id: managerId } : {})
      .then(() => { toast(managerId ? 'Placed under their manager.' : 'Added at the top level.'); onChanged?.(); })
      .catch((e) => { restoreSugg(removed); throw e; });
  };
  // Validate, don't place: real contact, parked on the bench.
  const benchSugg = (id) => {
    const removed = dropSugg(id);
    return apiClient.post(`/api/workspace/people-discovery/${id}/confirm`, { bench: true })
      .then(() => { toast('Validated — parked on the bench.'); onChanged?.(); })
      .catch((e) => { restoreSugg(removed); throw e; });
  };

  // "Add all" opens a DRY-RUN preview (what would happen + a confidence
  // threshold) before committing — with an Undo after.
  const openBulkPreview = (threshold) => {
    setBulkPreview({ loading: true, threshold: threshold || 0, data: null });
    const body = { department_id: departmentId, dry_run: true };
    if (threshold) body.min_confidence = threshold;
    apiClient.post('/api/workspace/people-discovery/confirm-all', body)
      .then((r) => setBulkPreview({ loading: false, threshold: threshold || 0, data: r }))
      .catch((e) => { toast(humanErr(e), 'error'); setBulkPreview(null); });
  };
  const acceptAllSugg = () => { openBulkPreview(0); return Promise.resolve(); };
  const proceedBulk = () => {
    const threshold = bulkPreview ? bulkPreview.threshold : 0;
    setBulkBusy(true);
    const body = { department_id: departmentId };
    if (threshold) body.min_confidence = threshold;
    apiClient.post('/api/workspace/people-discovery/confirm-all', body)
      .then((r) => {
        const n = (r.confirmed || 0) + (r.merged || 0);
        toast(`Added ${r.confirmed || 0}${r.merged ? `, merged ${r.merged}` : ''}${threshold ? ` (≥${threshold}%)` : ''}.`);
        setLastBulk(n > 0 ? { kind: 'confirm', created: r.createdContactIds || [], resolved: r.resolvedCandidateIds || [], label: `Added ${n}` } : null);
        setBulkPreview(null);
        onChanged?.();
        return refreshSugg();
      })
      .catch((e) => toast(humanErr(e), 'error'))
      .finally(() => setBulkBusy(false));
  };
  const loadStructure = useCallback(() => {
    setStructLoading(true);
    return apiClient.get(`/api/workspace/people-discovery/structure?${deptQs}`)
      .then((r) => setStructure({ people: r?.people || [], stats: r?.stats || {} }))
      .catch(() => setStructure({ people: [], stats: {} }))
      .finally(() => setStructLoading(false));
  }, [deptQs]);
  const undoLastBulk = () => {
    if (!lastBulk) return;
    setUndoBusy(true);
    const call = lastBulk.kind === 'confirm'
      ? apiClient.post('/api/workspace/people-discovery/undo-bulk', { created_contact_ids: lastBulk.created, resolved_candidate_ids: lastBulk.resolved })
      : apiClient.post('/api/workspace/people-discovery/undo-structure', { child_contact_ids: lastBulk.wiredChildIds || [] });
    call
      .then(() => {
        toast('Undone.');
        setLastBulk(null);
        onChanged?.();
        return Promise.all([refreshSugg(), structure ? loadStructure() : null]);
      })
      .catch((e) => toast(humanErr(e), 'error'))
      .finally(() => setUndoBusy(false));
  };
  const toggleStructure = () => { if (structure) setStructure(null); else loadStructure(); };
  // Recall discovered reporting lines onto the confirmed chart — gap-fill only.
  const applyStructure = () => {
    setApplyingStruct(true);
    apiClient.post('/api/workspace/people-discovery/apply-structure', { department_id: departmentId })
      .then((r) => {
        if (r.wired > 0) {
          toast(`Wired ${r.wired} reporting line${r.wired === 1 ? '' : 's'} from the discovered chart.`);
          setLastBulk({ kind: 'structure', wiredChildIds: r.wiredChildIds || [], label: `Wired ${r.wired}` });
        } else {
          toast('Nothing to wire — every discovered edge is already placed or its people aren’t on the chart yet.', 'info');
        }
        onChanged?.();
        return loadStructure();
      })
      .catch((e) => toast(humanErr(e), 'error'))
      .finally(() => setApplyingStruct(false));
  };

  /* ── the company-contacts bucket + CRM-wide search ──────────────────── */

  const search = () => {
    if (q.trim().length < 2) return;
    setBusy(true);
    apiClient.get(`/api/workspace/contacts/search?q=${encodeURIComponent(q.trim())}`)
      .then((r) => setResults(r?.contacts || []))
      .catch(() => setResults([]))
      .finally(() => setBusy(false));
  };
  const addExisting = (contactId) => {
    setBusy(true);
    const body = accountId ? { account_id: accountId } : { department_id: departmentId };
    apiClient.post(`/api/workspace/contacts/${contactId}/home`, body)
      .then((r) => {
        setQ(''); setResults(null);
        // Re-home clears a stale reporting line but never benches anyone, so
        // they land on the canvas as a top-level card, ready to be dragged.
        toast('Pulled onto the canvas — drag them onto their manager.'
          + (r?.reportsToCleared ? ' Their old reporting line was cleared.' : ''));
        onChanged?.();
        loadRoster();
      })
      .catch((e) => toast(humanErr(e), 'error'))
      .finally(() => setBusy(false));
  };

  const inChart = new Set(contacts.map((c) => c.id));
  const matches = results ? results.filter((c) => !inChart.has(c.id)) : null;
  const isEmpty = mapped.length === 0;
  const seedRows = parseSeed(seedText).length;
  const compRows = compText.trim() ? Math.max(compText.trim().split('\n').length - 1, 0) : 0;

  // The bucket rows. Everyone the CRM ties to this company, ordered so the
  // actionable ones (not on this account yet) come first, then the bench,
  // then the canvas. Placement for anyone already on this account is read
  // from the LIVE contacts prop — including the optimistic bench/delete
  // overlay — so a chip never contradicts the card next to it.
  const bucket = useMemo(() => {
    const rows = (roster || []).map((r) => {
      const live = byId.get(r.id);
      if (gone[r.id]) return null;
      const placement = live
        ? ((benched[r.id] || live.stashed_at) ? 'bench' : 'chart')
        : r.placement;
      return { ...r, placement };
    }).filter(Boolean);
    // A contact the workspace knows about that the roster read missed (a
    // brand-new one, added since the last load) still belongs in the list.
    const seen = new Set(rows.map((r) => r.id));
    for (const c of contacts) {
      if (seen.has(c.id) || gone[c.id]) continue;
      rows.push({
        id: c.id,
        firstName: c.first_name,
        lastName: c.last_name,
        title: c.title,
        email: c.email,
        orgUnit: c.org_unit,
        accountId,
        accountName: null,
        why: 'account',
        placement: (benched[c.id] || c.stashed_at) ? 'bench' : 'chart',
      });
    }
    const rank = { off_account: 0, bench: 1, chart: 2 };
    return rows.sort((a, b) => (rank[a.placement] - rank[b.placement])
      || String(a.lastName || a.firstName || '').localeCompare(String(b.lastName || b.firstName || '')));
  }, [roster, contacts, byId, gone, benched, accountId]);

  const offAccount = bucket.filter((r) => r.placement === 'off_account').length;

  const PLACEMENT = {
    chart: { label: 'On chart', cls: '' },
    bench: { label: 'Bench', cls: 'aw-org-chip--warn' },
    off_account: { label: 'In CRM', cls: 'aw-org-chip--accent' },
  };
  const WHY = {
    account: 'Homed on this account',
    linked: 'Covered on this account',
    department: 'Tagged to this department',
    domain: 'Email is on this company’s domain',
  };

  const bucketRow = (r) => {
    const name = `${r.firstName || ''} ${r.lastName || ''}`.trim() || '(no name)';
    const meta = [r.title, r.orgUnit, r.email].filter(Boolean).join(' · ');
    const place = PLACEMENT[r.placement] || PLACEMENT.chart;
    const clickable = r.placement !== 'off_account' && onContactClick && byId.has(r.id);
    return (
      <div className="aw-org-row" key={r.id}>
        <div className="aw-org-row-main">
          <div className="aw-org-row-title">
            {clickable ? (
              <button type="button" className="aw-org-linklike" onClick={() => onContactClick(byId.get(r.id))}>
                {name}
              </button>
            ) : name}
            <span className={`aw-org-chip ${place.cls}`} title={WHY[r.why] || ''}>{place.label}</span>
            {r.placement === 'off_account' && r.accountName && (
              <span className="aw-org-badge">{r.accountName}</span>
            )}
          </div>
          {meta && <div className="aw-org-row-meta">{meta}</div>}
        </div>
        {r.placement === 'off_account' && (
          <button
            type="button"
            className="aw-org-btn"
            disabled={busy}
            title="Re-home this contact to this account and put them on the canvas"
            onClick={() => addExisting(r.id)}
          >
            <UserPlus size={13} /> Add to chart
          </button>
        )}
      </div>
    );
  };

  // The bucket: a standing roster of the company's people, shown whether or
  // not the canvas has anyone on it, with CRM-wide search folded in for the
  // people the company ties don't reach.
  const scoped = rosterScope === 'off-account'
    ? bucket.filter((r) => r.placement === 'off_account')
    : bucket;
  const offAccountOnly = rosterScope === 'off-account';
  const pullBlock = (
    <div className="aw-org-pull">
      <div className="aw-org-pull-lead">
        <Users size={13} />
        <strong>{offAccountOnly ? 'Elsewhere in the CRM' : 'Company contacts'}</strong>
        <span className="aw-org-badge">{scoped.length}</span>
        <span className="aw-org-note">
          {rosterErr
            ? 'Roster unavailable right now.'
            : roster === null
              ? 'Loading…'
              : offAccountOnly
                // The count is the same fact either way; only the baseline it
                // is stated against changes, because the directory beside this
                // already accounts for everyone homed here.
                ? (offAccount > 0
                  ? `${offAccount} in the CRM under another account — add them, then drag onto the canvas.`
                  : 'Nobody in the CRM is missing from this account.')
                : bucket.length === 0
                  ? 'Nobody in the CRM for this company yet.'
                  : offAccount > 0
                    ? `${offAccount} not on this chart yet — add them, then drag onto the canvas.`
                    : 'Everyone the CRM has for this company is on the chart.'}
        </span>
        <span className="aw-org-toolbar-spacer" />
        <button type="button" className="aw-org-linklike" disabled={busy} onClick={() => setPullOpen((o) => !o)}>
          {pullOpen ? 'Hide search' : 'Search all CRM contacts →'}
        </button>
      </div>

      {scoped.length > 0 && (
        <div className="aw-org-pull-results aw-org-bucket-list">{scoped.map(bucketRow)}</div>
      )}

      {pullOpen && (
        <>
          <div className="aw-org-pull-row">
            <input
              value={q}
              placeholder="Search the whole CRM by name or email…"
              disabled={busy}
              autoFocus
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
            />
            <button type="button" className="aw-org-btn" disabled={busy || q.trim().length < 2} onClick={search}>
              <Search size={13} /> Find
            </button>
          </div>
          {matches && (
            <div className="aw-org-pull-results">
              {matches.length === 0 && (
                <div className="aw-org-note">No CRM contacts match “{q}” that aren’t already on this chart.</div>
              )}
              {matches.map((c) => (
                <div className="aw-org-row" key={c.id}>
                  <div className="aw-org-row-main">
                    <div className="aw-org-row-title">
                      {`${c.firstName || ''} ${c.lastName || ''}`.trim()}
                      {c.accountName && <span className="aw-org-badge">{c.accountName}</span>}
                    </div>
                    <div className="aw-org-row-meta">{c.title || ''}{c.email ? `${c.title ? ' · ' : ''}${c.email}` : ''}</div>
                  </div>
                  <button
                    type="button"
                    className="aw-org-btn"
                    disabled={busy}
                    title="Re-home this contact to this account and place them on the chart"
                    onClick={() => addExisting(c.id)}
                  >
                    <UserPlus size={13} /> Add to chart
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  const panel = (
    <div className={`aw-orgchart-wrap ${fs ? 'aw-orgchart-wrap--fs' : ''}`}>
      <div className="aw-org-toolbar" role="toolbar" aria-label="Org chart tools">
        {onAddContact && (
          <button type="button" className="aw-org-btn aw-org-btn--primary" disabled={busy} onClick={() => onAddContact(null)}>
            <UserPlus size={13} /> New contact
          </button>
        )}
        {canDiscover && (
          <>
            <button
              type="button"
              className="aw-org-btn"
              disabled={busy || discovering}
              onClick={discover}
              title="Find this agency's tech leadership (CIO, CISO, directors…) and add them as suggestions to review"
            >
              <Sparkles size={13} /> {discovering ? 'Discovering…' : 'Discover people'}
            </button>
            <button
              type="button"
              className={`aw-org-btn ${seedOpen ? 'aw-org-btn--on' : ''}`}
              disabled={busy || discovering}
              onClick={() => setSeedOpen((o) => !o)}
              title="Paste a published leadership directory to seed discovery (Name, Title per line)"
            >
              <ClipboardPaste size={13} /> {seedOpen ? 'Hide paste' : 'Paste directory…'}
            </button>
            <button
              type="button"
              className={`aw-org-btn ${compOpen ? 'aw-org-btn--on' : ''}`}
              disabled={busy || discovering}
              onClick={() => setCompOpen((o) => !o)}
              title="Paste a PUBLIC position roster (State Controller GCC / Transparent California export) — builds the full org shape and folds the discovered leaders onto it"
            >
              <Table2 size={13} /> {compOpen ? 'Hide roster' : 'Paste roster…'}
            </button>
            <button
              type="button"
              className={`aw-org-btn ${structure ? 'aw-org-btn--on' : ''}`}
              disabled={busy || structLoading}
              onClick={toggleStructure}
              title="What discovery found for this agency and how it hangs together — recall the reporting lines onto your chart"
            >
              <GitBranch size={13} /> {structure ? 'Hide structure' : (structLoading ? 'Loading…' : 'Discovered structure…')}
            </button>
          </>
        )}
        {accountId && (
          <button type="button" className={`aw-org-btn ${trash ? 'aw-org-btn--on' : ''}`} disabled={busy} onClick={() => (trash ? setTrash(null) : loadTrash())}>
            <Trash2 size={13} /> {trash ? 'Hide trash' : 'Trash…'}
          </button>
        )}
        <span className="aw-org-toolbar-spacer" />
        <button
          type="button"
          className="aw-org-btn"
          title={fs ? 'Exit fullscreen (Esc)' : 'Fullscreen — room to work the whole tree'}
          aria-pressed={fs}
          onClick={() => setFs((x) => !x)}
        >
          {fs ? <Minimize2 size={13} /> : <Maximize2 size={13} />} {fs ? 'Exit fullscreen' : 'Fullscreen'}
        </button>
      </div>

      {seedOpen && (
        <div className="aw-org-panel">
          <div className="aw-org-note">
            Paste a public leadership directory — one person per line as <code>Name, Title</code>.
            Non-tech roles are filtered out automatically; each person becomes a reviewable suggestion.
          </div>
          <textarea
            className="aw-org-textarea"
            value={seedText}
            disabled={discovering}
            rows={5}
            placeholder={'Liana Bailey-Crimmins, State Chief Information Officer\nVitaliy Panych, Chief Information Security Officer\nJane Doe, Deputy Director of Technology'}
            onChange={(e) => setSeedText(e.target.value)}
          />
          <div className="aw-org-actions">
            <button type="button" className="aw-org-btn aw-org-btn--primary" disabled={discovering || !seedRows} onClick={discover}>
              <Sparkles size={13} /> {discovering ? 'Discovering…' : `Discover from paste${seedRows ? ` (${seedRows})` : ''}`}
            </button>
            <button type="button" className="aw-org-btn" disabled={discovering} onClick={() => { setSeedOpen(false); setSeedText(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {compOpen && (
        <div className="aw-org-panel">
          <div className="aw-org-note">
            Paste a <strong>public position roster</strong> — export the agency's employees from{' '}
            <a href="https://transparentcalifornia.com/" target="_blank" rel="noopener noreferrer">Transparent California</a>{' '}
            or the <a href="https://publicpay.ca.gov/" target="_blank" rel="noopener noreferrer">State Controller (GCC)</a> and paste the CSV
            (needs a <code>Position</code>/job-title column; a name column is optional). Discovery
            builds the full tiered org shape and folds the discovered CIO/ISO onto it. Public data only.
          </div>
          <textarea
            className="aw-org-textarea aw-org-textarea--mono"
            value={compText}
            disabled={discovering}
            rows={6}
            placeholder={'Position,Total Wages\nChief Information Officer,182000\nInformation Technology Manager I,158000\nInformation Technology Specialist II,110000'}
            onChange={(e) => setCompText(e.target.value)}
          />
          <div className="aw-org-actions">
            <button type="button" className="aw-org-btn aw-org-btn--primary" disabled={discovering || !compText.trim()} onClick={discover}>
              <Table2 size={13} /> {discovering ? 'Building…' : 'Discover + build roster'}
            </button>
            <button type="button" className="aw-org-btn" disabled={discovering} onClick={() => { setCompOpen(false); setCompText(''); }}>Cancel</button>
            {compRows > 0 && <span className="aw-org-note">{compRows} rows</span>}
          </div>
        </div>
      )}

      {rosterBackbone && (
        <div className="aw-org-panel aw-org-panel--framed">
          <div className="aw-org-panel-head">
            <strong><Table2 size={13} /> Roster backbone</strong>
            <span className="aw-org-note">
              {(rosterBackbone.totals && rosterBackbone.totals.positions) || 0} positions ·{' '}
              {(rosterBackbone.totals && rosterBackbone.totals.itPositions) || 0} IT ·{' '}
              {(rosterBackbone.totals && rosterBackbone.totals.techLeaders) || 0} tech leaders
            </span>
            <span className="aw-org-toolbar-spacer" />
            <button type="button" className="aw-org-btn" onClick={() => setRosterBackbone(null)}>Dismiss</button>
          </div>
          {rosterBackbone.source && (
            <div className="aw-org-note">
              Source: {rosterBackbone.source.kind === 'calhr_5102' ? 'CalHR 5102 workforce data (auto)' : 'pasted roster'}
              {rosterBackbone.source.asOf ? ` · as of ${String(rosterBackbone.source.asOf).slice(0, 4)}` : ''}
              {rosterBackbone.source.department ? ` · ${rosterBackbone.source.department}` : ''}
            </div>
          )}
          <div className="aw-org-note">Discovered leaders folded onto the comp-derived org shape:</div>
          <div className="aw-org-list">
            {(rosterBackbone.leadershipSpine || []).slice(0, 12).map((p, i) => (
              <div key={i} className="aw-org-list-row">
                <span className="aw-org-badge aw-org-badge--tier">{p.tier}</span>
                <span className="aw-org-list-title">{p.roleTitle && p.roleTitle !== p.position ? p.roleTitle : p.position}</span>
                <span className="aw-org-toolbar-spacer" />
                {p.name
                  ? <span className="aw-org-list-name">{p.name}{p.partialName ? <span className="aw-org-note"> (partial)</span> : ''}{p.nameSource ? <span className="aw-org-note"> · {p.nameSource}</span> : ''}</span>
                  : <span className="aw-org-note aw-org-note--em">unnamed — comp slot</span>}
              </div>
            ))}
          </div>
          <div className="aw-org-note">
            Named leaders come from the CDT directories / discovery; the unnamed slots are the org’s shape
            (positions, no names in the public data). Nothing here is written to the CRM — confirm people from the suggestions.
          </div>
        </div>
      )}

      {discoverResult && (
        <div className="aw-org-strip" role="status">
          <span className="aw-org-badge aw-org-badge--ok">Found {discoverResult.found}</span>
          <span className="aw-org-note">matched {discoverResult.matched} existing · {discoverResult.new} new</span>
          {discoverResult.stats && discoverResult.stats.non_tech_filtered > 0 && (
            <span className="aw-org-note">· filtered {discoverResult.stats.non_tech_filtered} non-tech</span>
          )}
          {discoverResult.providers && discoverResult.providers.enrichment && !discoverResult.providers.enrichment.configured && (
            <span className="aw-org-note" title="No enrichment provider configured — each person gets a LinkedIn search link to click">
              · LinkedIn: search links (no provider)
            </span>
          )}
          <span className="aw-org-toolbar-spacer" />
          <button type="button" className="aw-org-btn" onClick={() => setDiscoverResult(null)} aria-label="Dismiss"><X size={13} /></button>
        </div>
      )}

      {structure && (
        <div className="aw-org-panel">
          <div className="aw-org-panel-head">
            <strong><GitBranch size={13} /> Discovered structure</strong>
            <span className="aw-org-note">
              {structure.stats.total || 0} discovered · {structure.stats.onChart || 0} on chart
              · {structure.stats.suggested || 0} pending · {structure.stats.dismissed || 0} dismissed
            </span>
            <span className="aw-org-toolbar-spacer" />
            <button
              type="button"
              className="aw-org-btn aw-org-btn--primary"
              disabled={applyingStruct || !(structure.stats.recallableEdges > 0)}
              title={structure.stats.recallableEdges > 0
                ? 'Wire discovered reporting lines onto confirmed contacts — fills gaps only, never overrides a placement you made'
                : 'No recallable lines yet — both a person and their discovered manager must be on the chart'}
              onClick={applyStructure}
            >
              {applyingStruct ? 'Applying…' : `Apply reporting lines${structure.stats.recallableEdges ? ` (${structure.stats.recallableEdges})` : ''}`}
            </button>
          </div>
          {structure.people.length === 0 ? (
            <div className="aw-org-note">Nothing discovered for this agency yet — run <strong>Discover people</strong>.</div>
          ) : (
            <div className="aw-org-list aw-org-list--scroll">
              {structure.people.map((p) => {
                const m = STRUCT_STATUS[p.status] || { label: p.status, tone: 'muted' };
                return (
                  <div key={p.id} className="aw-org-list-row aw-org-list-row--rule">
                    <span className={`aw-org-dot aw-org-dot--${m.tone}`} title={m.label} />
                    <span className="aw-org-list-name">{p.fullName}</span>
                    <span className="aw-org-note aw-org-list-title">{p.title || ''}</span>
                    {p.managerName && <span className="aw-org-note" title="Discovered reporting line">↳ {p.managerName}</span>}
                    <span className="aw-org-toolbar-spacer" />
                    <span className={`aw-org-status aw-org-status--${m.tone}`}>{m.label}</span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="aw-org-note">
            Recall wires confirmed contacts to their discovered manager — but only where you haven’t already placed them, so it never overrides your calls.
          </div>
        </div>
      )}

      {bulkPreview && (
        <div className="aw-org-panel aw-org-panel--accent" role="dialog" aria-label="Preview — add discovered people">
          <div className="aw-org-panel-head">
            <strong>Preview — add discovered people</strong>
            <span className="aw-org-toolbar-spacer" />
            <button type="button" className="aw-org-btn" disabled={bulkBusy} onClick={() => setBulkPreview(null)}>Cancel</button>
          </div>
          <div className="aw-org-actions">
            <span className="aw-org-note">Confidence ≥</span>
            {[0, 50, 70, 85].map((t) => (
              <button
                key={t}
                type="button"
                className={`aw-org-btn ${bulkPreview.threshold === t ? 'aw-org-btn--primary' : ''}`}
                disabled={bulkPreview.loading || bulkBusy}
                onClick={() => openBulkPreview(t)}
              >
                {t === 0 ? 'All' : `${t}%`}
              </button>
            ))}
          </div>
          {bulkPreview.loading ? (
            <div className="aw-org-note">Calculating…</div>
          ) : bulkPreview.data ? (
            <>
              <div className="aw-org-note">
                Will add <strong>{bulkPreview.data.wouldConfirm}</strong> new
                {bulkPreview.data.wouldMerge > 0 ? <> · merge <strong>{bulkPreview.data.wouldMerge}</strong> into existing</> : null}
                {` — ${bulkPreview.data.eligible} of ${bulkPreview.data.total} pending${bulkPreview.threshold > 0 ? ` meet ≥${bulkPreview.threshold}%` : ''}. Reporting lines wire automatically.`}
              </div>
              <div className="aw-org-actions">
                <button
                  type="button"
                  className="aw-org-btn aw-org-btn--primary"
                  disabled={bulkBusy || (bulkPreview.data.wouldConfirm + bulkPreview.data.wouldMerge) === 0}
                  onClick={proceedBulk}
                >
                  {bulkBusy ? 'Adding…' : `Add ${bulkPreview.data.wouldConfirm + bulkPreview.data.wouldMerge} to chart`}
                </button>
                <button type="button" className="aw-org-btn" disabled={bulkBusy} onClick={() => setBulkPreview(null)}>Cancel</button>
              </div>
            </>
          ) : null}
        </div>
      )}

      {lastBulk && (
        <div className="aw-org-strip aw-org-strip--accent" role="status">
          <span className="aw-org-note"><strong>{lastBulk.label}</strong> to the chart — you can undo this.</span>
          <span className="aw-org-toolbar-spacer" />
          <button type="button" className="aw-org-btn" disabled={undoBusy} onClick={undoLastBulk}><Undo2 size={13} /> {undoBusy ? 'Undoing…' : 'Undo'}</button>
          <button type="button" className="aw-org-btn" disabled={undoBusy} onClick={() => setLastBulk(null)}>Dismiss</button>
        </div>
      )}

      {(!isEmpty || sugg.length > 0) ? (
        <>
          <div className="aw-org-stage">
            <SharedOrgChart
              contacts={mapped}
              suggestions={sugg}
              onReparent={handleReparent}
              onDelete={handleDelete}
              onStash={handleStash}
              onMerge={handleMerge}
              onVerify={handleVerify}
              onConfirmSuggestion={canDiscover ? confirmSugg : undefined}
              onMergeSuggestion={canDiscover ? mergeSugg : undefined}
              onRejectSuggestion={canDiscover ? rejectSugg : undefined}
              onAcceptAllSuggestions={canDiscover ? acceptAllSugg : undefined}
              onPlaceSuggestion={canDiscover ? placeSugg : undefined}
              onBenchSuggestion={canDiscover ? benchSugg : undefined}
              onAddContact={onAddContact ? (node) => onAddContact(byId.get(node.id) || node) : undefined}
              onContactClick={onContactClick ? (node) => onContactClick(byId.get(node.id) || node) : undefined}
              fullscreen={fullscreen || fs}
              focusRequest={focusRequest}
            />
          </div>
          {pullBlock}
        </>
      ) : (
        // Empty state = a blank canvas with the one primary CTA and the
        // discovery path — "Discover people" populates suggestions without
        // adding anyone by hand.
        <div className="aw-org-empty-hero">
          <Network size={64} strokeWidth={1} />
          <h2>Build {departmentLabel ? `${departmentLabel}'s` : 'the'} org chart</h2>
          <p>
            Map who runs this account. Add the first person, or use <strong>Discover people</strong> to
            surface this agency's tech leadership, then drag contacts to set who reports to whom.
          </p>
          <div className="aw-org-actions">
            {onAddContact && (
              <button type="button" className="aw-org-empty-btn" onClick={() => onAddContact(null)}>
                <UserPlus size={16} /> Add first contact
              </button>
            )}
            {canDiscover && (
              <button type="button" className="aw-org-empty-btn aw-org-empty-btn--secondary" disabled={discovering} onClick={discover}>
                <Sparkles size={16} /> {discovering ? 'Discovering…' : 'Discover people'}
              </button>
            )}
          </div>
          {pullBlock}
        </div>
      )}

      {trash && (
        <div className="aw-org-trash">
          {trash.length === 0 && <div className="aw-org-trash-empty">Trash is empty.</div>}
          {trash.map((c) => (
            <div className="aw-org-trash-row" key={c.id}>
              <div className="aw-org-trash-name">
                {`${c.first_name || ''} ${c.last_name || ''}`.trim()}
                {c.title ? <span className="aw-org-trash-title"> — {c.title}</span> : null}
                {c.deleted_at ? (
                  <span className="aw-org-trash-when"> · deleted {String(c.deleted_at).slice(0, 10)}</span>
                ) : null}
              </div>
              <button
                type="button"
                className="aw-org-trash-restore"
                title="Restore to the bench (drag into place afterwards)"
                onClick={() => handleRestore(c.id).catch(() => {})}
              >
                <Undo2 size={12} /> Restore
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Fullscreen: same panel, fixed overlay — room to work a wide tree.
  if (fs) return <div className="aw-org-fs">{panel}</div>;
  return panel;
}

export default OrgChart;
